import { Rectangle } from "recharts";
import { ActionRef } from "./ActionRef";
import { Canvas } from "./Canvas";


export type ContextMenuTargetRect = {top:number,bottom:number,left:number,right:number};
export type ContextMenuHandlerArgs = {rect:ContextMenuTargetRect,target:HTMLElement};

/**
 * Right clicking a field or a selection means cut/copy/paste/spellcheck - the browser menu is
 * the only thing that offers those, so never replace it there.
 */
export function isEditableTarget(target:HTMLElement):boolean{
    if (!target) return false;
    return !!target.closest('input,textarea,select,[contenteditable=""],[contenteditable="true"]');
}

/**
 * True only when the click landed on the selection itself. A selection left somewhere else on
 * the page must not stop a menu from opening here.
 */
export function hasTextSelectionAt(target:HTMLElement):boolean{
    if (!target) return false;
    let selection = window.getSelection();
    if (!selection || selection.isCollapsed) return false;
    if (!selection.toString().length) return false;
    for(let i = 0; i < selection.rangeCount; i++){
        if (selection.getRangeAt(i).intersectsNode(target)) return true;
    }
    return false;
}

export class ContextMenuHandler {
    public id:string;
    public handleOpen:(args:ContextMenuHandlerArgs) => void;
    public onClose:() => void;
    public isOpen:boolean;
    public canvas:Canvas;
    public row:any;
    public elemId:string;
    public x:number;
    public y:number;
    public rect:ContextMenuTargetRect;

    public manager:ContextMenuManager;

    public open(){
        this.isOpen = true;
        this.canvas.update();
    }
    public close(){
        this.isOpen = false;
        if (this.onClose){
            this.onClose()
        }
        this.canvas.update();
    }

    public trigger(action:string,scope:any){
        this.isOpen = false;
        this.canvas.update();
        let actionRef = new ActionRef(this.canvas,action,scope);
        actionRef.trigger();
    }
}


export class ContextMenuManager {
    private _handlers:ContextMenuHandler[] = [];
    private _nextId = 1;

    public createHandler(canvas:Canvas,handleOpen:(args:ContextMenuHandlerArgs) => void):ContextMenuHandler{
        let handler = new ContextMenuHandler();
        handler.canvas = canvas;
        handler.id = (this._nextId++).toString();
        handler.handleOpen = handleOpen;
        return handler;
    }

    public connect(handler:ContextMenuHandler){
       this._handlers.push(handler);
    }
    
    public disconnect(handler:ContextMenuHandler){
        let i = this._handlers.indexOf(handler);
        if (i != -1){
            this._handlers.splice(i,1);
        }
    }

    /**
     * The handler a right click would open, or null if there is none. Callers resolve before
     * suppressing the browser menu - preventDefault on a click that opens nothing is what makes
     * copy/paste disappear for no gain.
     *
     * An element opts in with data-context-menu, or with data-context-menu-alt to ask that a
     * plain right click be left to the browser and the menu open only with Alt held. The nearest
     * of the two wins, so a header can claim the right click inside a layer that does not.
     */
    public resolve(target:HTMLElement,hasModifier:boolean):ContextMenuHandler{
        if (!target) return null;
        let handlerElem = target.closest('[data-context-menu],[data-context-menu-alt]');
        if (!handlerElem) return null;
        let handlerId = handlerElem.getAttribute('data-context-menu');
        if (!handlerId){
            if (!hasModifier) return null;
            handlerId = handlerElem.getAttribute('data-context-menu-alt');
        }
        return this.findHandler(handlerId);
    }

    public handleOpen(args:ContextMenuHandlerArgs){
        // opened from a button rather than a right click, so the modifier does not apply
        let handler = this.resolve(args.target,true);
        if (handler){
            handler.handleOpen(args);
        }
    }

    private findHandler(id:string):ContextMenuHandler {
        for(let i =0 ; i < this._handlers.length;i++){
            let handler = this._handlers[i];
            if (handler.id == id) return handler;
        }
        return null;
    }

    public closeAll(){
        for(let i =0 ; i < this._handlers.length;i++){
            let handler = this._handlers[i];
            if (handler.isOpen){
                handler.close();
            }
        }
        return null;
    }
}